import { MutationFunction, useMutation, useQueryClient } from "react-query";

import axios from "axios";

import { makeRequest } from "../../util";
import { BAAUploadLinkResponse, BAAResponse } from "../types";
import useProductEventEmitter from "../instrumentation/useProductEventEmitter";

export type saveBAAArgs = {
  file: File;
  siteDomain: string;
  trackerDomain: string;
  trackerName: string;
};

async function saveBAA({
  file,
  siteDomain,
  trackerDomain,
}: saveBAAArgs): Promise<BAAResponse> {
  // get link to upload file to s3
  const getLinkResp = await makeRequest<BAAUploadLinkResponse>(
    "get_baa_upload_link",
    {
      fileContentLength: file.size,
      fileName: file.name,
    }
  );
  const uploadLink = getLinkResp.data.uploadLink;
  const s3ObjectKey = getLinkResp.data.s3ObjectKey; //gitleaks:allow

  // upload file to s3
  await axios.request({
    method: "put",
    url: uploadLink,
    data: file,
  });

  // send baa upload metadata to server
  const saveMetadataResp = await makeRequest<BAAResponse>("save_baa", {
    siteDomain,
    trackerDomain,
    s3ObjectKey,
  });
  return {
    webTrackerBAA: {
      ...saveMetadataResp.data.webTrackerBAA,
      uploadedAt: new Date(saveMetadataResp.data.webTrackerBAA.uploadedAt),
    },
    downloadLink: saveMetadataResp.data.downloadLink,
  };
}

export default function useSaveBAA(onSuccess: () => void, onError: () => void) {
  const queryClient = useQueryClient();
  const productEventEmitter = useProductEventEmitter();

  return useMutation<BAAResponse, unknown, saveBAAArgs>(
    saveBAA as MutationFunction<BAAResponse, saveBAAArgs>,
    {
      onSuccess: (_, variables) => {
        void queryClient.invalidateQueries(
          `baa-${variables.siteDomain}-${variables.trackerDomain}`
        );

        void queryClient.invalidateQueries(`web-tracker-latest-report`);

        void productEventEmitter.trackBAAUploaded(
          variables.trackerName,
          variables.trackerDomain
        );

        onSuccess();
      },
      onError: onError,
    }
  );
}
